import {Flag, Tabs, type TabProps} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import type {DailyStockTakeDetailResponse} from '@dms/api/reporting';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, type Nullish, type RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useDailyStockTakeReport} from '../hooks/useDailyStockTakeReport';
import {buildDailyStockTakeDetailView} from '../utils/buildDailyStockTakeDetailView';
import {DailyStockTakeMovements} from './DailyStockTakeMovements';
import {StockTakeReportButton} from './StockTakeReportButton';

const COMPLETED_TAB = 'completed';
const IN_PROGRESS_TAB = 'inProgress';

interface DailyStockTakeDetailContentProps extends RequiredTestIdProps {
  detail: DailyStockTakeDetailResponse;
  currency: string | Nullish;
}

export function DailyStockTakeDetailContent(props: DailyStockTakeDetailContentProps) {
  /**
   * Controlled on purpose: left to itself `Tabs` keeps the active tab in a URL query param,
   * which a dialog opened over a datagrid does not own — the grid rewrites the query on its own
   * and the tab snaps back.
   */
  const [activeTab, setActiveTab] = useState(COMPLETED_TAB);

  const view = buildDailyStockTakeDetailView(props.detail, props.currency);

  const {money, count, day, signed, differencePercent, cardsWithValue, check, report} =
    useDailyStockTakeReport(view, props.detail.id);

  /**
   * The arithmetic the check verdict is drawn from. It stays on screen as one compact line
   * rather than behind a hover: the verdict is nullable — the closing job reports none for a
   * warehouse's first day or across a gap in closing days — and hanging these four figures off
   * the badge would take them away on exactly the days the reader most needs to add them up
   * themselves. A hover would also put them out of reach on a touch screen.
   *
   * The corrections figure is looked up by id rather than by position: `checkItems` is built in
   * another file, and an index would keep compiling while pointing at a different line.
   */
  const checkLines = [
    {
      id: 'receipts',
      label: i18n.t('entity.warehouse.reporting.dailyReceipts'),
      amount: view.receiptTotal,
    },
    {
      id: 'issues',
      label: i18n.t('entity.warehouse.reporting.dailyIssues'),
      amount: view.issueTotal,
    },
    {
      id: 'corrections',
      label: i18n.t('entity.warehouse.reporting.correctionsAndAdjustments'),
      amount: view.checkItems.find((item) => item.id === 'corrections')?.amount ?? null,
    },
    {
      id: 'total',
      label: i18n.t('entity.warehouse.reporting.checkTotal'),
      amount: view.checkSum,
    },
  ];

  const checkSummary = (
    <HStack spacing={2} wrap>
      {checkLines.map((line) => (
        <Text
          key={line.id}
          size="xSmall"
          color="secondary"
          data-testid={suffixTestId(`checkLine-${line.id}`, props)}
        >
          {`${line.label}: ${money(line.amount)}`}
        </Text>
      ))}
    </HStack>
  );

  const tabs: TabProps[] = [
    {
      id: COMPLETED_TAB,
      title: i18n.t('entity.warehouse.reporting.completedMovements'),
      content: (
        <DailyStockTakeMovements
          inputs={[
            {
              label: i18n.t('entity.warehouse.reporting.openingValue'),
              value: money(view.opening.amount),
              note: cardsWithValue(view.opening.articles),
            },
            {
              label: i18n.t('entity.warehouse.reporting.closingValue'),
              value: money(view.closing.amount),
              note: cardsWithValue(view.closing.articles),
            },
          ]}
          result={{
            label: i18n.t('entity.warehouse.reporting.inventoryDifference'),
            value: signed(view.difference),
            note: differencePercent,
          }}
          receiptItems={view.receiptItems}
          receiptTotalLabel={i18n.t('entity.warehouse.reporting.dailyReceipts')}
          receiptTotal={view.receiptTotal}
          receiptArticles={view.receiptArticles}
          issueItems={view.issueItems}
          issueTotalLabel={i18n.t('entity.warehouse.reporting.dailyIssues')}
          issueTotal={view.issueTotal}
          issueArticles={view.issueArticles}
          correctionTotalLabel={i18n.t('entity.warehouse.reporting.correctionsAndAdjustments')}
          correctionTotal={view.correctionTotal}
          correctionDocuments={view.correctionDocuments}
          data-testid={suffixTestId('completed', props)}
        />
      ),
    },
    {
      id: IN_PROGRESS_TAB,
      title: i18n.t('entity.warehouse.reporting.inProgressMovements'),
      content: (
        <DailyStockTakeMovements
          inputs={[
            {
              label: i18n.t('entity.warehouse.reporting.inProgressReceipts'),
              value: money(view.inProgressReceiptTotal),
            },
            {
              label: i18n.t('entity.warehouse.reporting.inProgressIssues'),
              value: money(view.inProgressIssueTotal),
            },
          ]}
          // No difference tile: it would set goods not yet received against goods not yet issued,
          // which is not a stock movement.
          receiptItems={view.inProgressReceiptItems}
          receiptTotalLabel={i18n.t('entity.warehouse.reporting.inProgressReceipts')}
          receiptTotal={view.inProgressReceiptTotal}
          issueItems={view.inProgressIssueItems}
          issueTotalLabel={i18n.t('entity.warehouse.reporting.inProgressIssues')}
          issueTotal={view.inProgressIssueTotal}
          correctionTotalLabel={i18n.t('entity.warehouse.reporting.inProgressCorrections')}
          correctionTotal={view.inProgressCorrectionTotal}
          data-testid={suffixTestId('inProgress', props)}
        />
      ),
    },
  ];

  return (
    <VStack spacing={2}>
      <HStack spacing={3} align="center" justify="space-between" wrap>
        <HStack spacing={3} align="center" wrap>
          <Text size="large" alternative data-testid={suffixTestId('warehouse', props)}>
            {view.warehouseLabel ?? EMPTY_PLACEHOLDER}
          </Text>
          <Text size="small" color="secondary" data-testid={suffixTestId('branchAndDay', props)}>
            {`${i18n.t('entity.warehouse.labels.branch')}: ${
              view.branchLabel ?? EMPTY_PLACEHOLDER
            } · ${day(view.closingDay)}`}
          </Text>
          {/* No badge at all when the job reported no verdict — an empty one reads as a bug. */}
          <Show when={isNotNil(check)}>
            <Flag
              isSubtle
              colorScheme={check?.colorScheme}
              label={check?.label ?? EMPTY_PLACEHOLDER}
              data-testid={suffixTestId('checkStatus', props)}
            />
          </Show>
        </HStack>
        <StockTakeReportButton report={report} />
      </HStack>

      {checkSummary}

      <Show when={view.hasFailedArticles}>
        <Text size="xSmall" color="danger" data-testid={suffixTestId('failedCards', props)}>
          {`${i18n.t('entity.warehouse.reporting.cardsFailingCheck')}: ${count(
            view.articlesCheckFailed
          )}`}
        </Text>
      </Show>

      <Tabs
        tabs={tabs}
        activeTabId={activeTab}
        onChange={setActiveTab}
        data-testid={suffixTestId('movements', props)}
      />

      <Show when={isNotNil(view.openingSourceDay) || view.hasDayGap}>
        <VStack spacing={1}>
          <Show when={isNotNil(view.openingSourceDay)}>
            <Text size="xSmall" color="secondary" data-testid={suffixTestId('carriedFrom', props)}>
              {i18n.t('entity.warehouse.reporting.openingCarriedFrom', {
                date: day(view.openingSourceDay),
              })}
            </Text>
          </Show>
          <Show when={view.hasDayGap}>
            <Text size="xSmall" color="warning" data-testid={suffixTestId('dayGap', props)}>
              {i18n.t('entity.warehouse.reporting.closingDayGapNote')}
            </Text>
          </Show>
        </VStack>
      </Show>
    </VStack>
  );
}
